

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Modal,
  Box,
} from "@mui/material";
import { Edit, Delete, Save, Close } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ThemedCustomStepper from "components/CustomStepper";
import InlineEditField from "components/CustonEditableInlineField/CustomEditableInlineField";
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import { formatDate } from "utils/DateTimeConversion";
import {
  commonIconButtonStyles,
  commonIconStyles,
} from "assets/theme/components/iconButtonStyles";
import { useAuth } from "auth/Auth";
import { UiDetailSection } from "components/v2/record/DetailSection";

const PAYMENT_STATUS_STEPS = ["Payment Pending", "Paid", "Failed", "Refunded"];

const PAYMENT_METHOD_OPTIONS = [
  { value: "Card", label: "Card" },
  { value: "BankTransfer", label: "Bank Transfer" },
  { value: "PayID", label: "PayID" },
  { value: "Cash", label: "Cash" },
  { value: "Other", label: "Other" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "Deposit", label: "Deposit" },
  { value: "Final Balance", label: "Final Balance" },
  { value: "Installment", label: "Installment" },
  { value: "Full Payment", label: "Full Payment" },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: "Customer", label: "Customer" },
  { value: "Supplier", label: "Supplier" },
  { value: "Adjustment", label: "Adjustment" },
  { value: "Refund", label: "Refund" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "Payment Pending", label: "Payment Pending" },
  { value: "Paid", label: "Paid" },
  { value: "Failed", label: "Failed" },
  { value: "Refunded", label: "Refunded" },
  { value: "PartiallyRefunded", label: "Partially Refunded" },
];

const formatAmount = (amount) =>
  amount || amount === 0
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(amount)
    : "-";

const PaymentTransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const nameFromState = location.state?.name;
  const { axiosInstance } = useAuth();

  const [transaction, setTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchTransaction();
  }, [id]);

  useEffect(() => {
    if (!transaction?.payment_status) return;
    const idx = PAYMENT_STATUS_STEPS.indexOf(transaction.payment_status);
    if (idx >= 0) {
      setActiveStep(idx);
    } else if (transaction.payment_status === "PartiallyRefunded") {
      setActiveStep(PAYMENT_STATUS_STEPS.indexOf("Refunded"));
    }
  }, [transaction?.payment_status]);

  const fetchTransaction = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/payment-transactions/${id}`);
      setTransaction(response.data);
    } catch (error) {
      console.error("Error fetching payment transaction:", error);
      setSnackbar({
        open: true,
        message: "Error loading payment transaction details",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldUpdate = (field, value) => {
    setUpdatedFields((prev) => ({ ...prev, [field]: value }));
    setTransaction((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiscardChanges = () => {
    setIsEditing(false);
    setUpdatedFields({});
    fetchTransaction();
  };

  const handleSaveChanges = async () => {
    if (Object.keys(updatedFields).length === 0) {
      setIsEditing(false);
      return;
    }
    setIsSaveLoading(true);
    try {
      await axiosInstance.put(`/payment-transactions/${id}`, updatedFields);
      setUpdatedFields({});
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: "Payment Transaction updated successfully",
        severity: "success",
      });
      fetchTransaction();
    } catch (error) {
      console.error("Error updating payment transaction:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || "Error updating payment transaction",
        severity: "error",
      });
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleStatusStepClick = async (stepIndex) => {
    const nextStatus = PAYMENT_STATUS_STEPS[stepIndex];
    if (!nextStatus || nextStatus === transaction?.payment_status) return;

    setActiveStep(stepIndex);
    setTransaction((prev) => ({ ...prev, payment_status: nextStatus }));

    if (isEditing) {
      setUpdatedFields((prev) => ({ ...prev, payment_status: nextStatus }));
      return;
    }

    try {
      await axiosInstance.put(`/payment-transactions/${id}`, { payment_status: nextStatus });
      setSnackbar({
        open: true,
        message: `Payment status updated to ${nextStatus}`,
        severity: "success",
      });
      fetchTransaction();
    } catch (error) {
      console.error("Error updating payment status:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || "Error updating payment status",
        severity: "error",
      });
      fetchTransaction();
    }
  };

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await axiosInstance.delete(`/payment-transactions/${id}`);
      setSnackbar({
        open: true,
        message: "Payment Transaction deleted successfully",
        severity: "success",
      });
      setTimeout(() => navigate("/payment-transactions"), 800);
    } catch (error) {
      console.error("Error deleting payment transaction:", error);
      setSnackbar({
        open: true,
        message: "Error deleting payment transaction",
        severity: "error",
      });
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleUserClick = (userId) => {
    if (!userId) return;
    navigate(`/users/${userId}/profile`);
  };

  const renderSection = (title, fields) => {
    // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
    const sectionBody = (
      <>
      <Grid container spacing={3}>
        {fields.map((item) => (
          <Grid item xs={12} md={6} key={item.field}>
            <InlineEditField
              label={item.label}
              value={transaction?.[item.field]}
              options={item.options || null}
              onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
              isEditing={isEditing && !item.readOnly}
              readOnly={item.readOnly || false}
              renderValue={
                item.field === "created_by_name" || item.field === "updated_by_name"
                  ? (val) => {
                      const userId =
                        item.field === "created_by_name"
                          ? transaction?.created_by
                          : transaction?.updated_by;
                      const label =
                        val !== null && val !== undefined && val !== "" ? String(val) : "-";
                      if (!userId || label === "-") return label;
                      return (
                        <MDTypography
                          component="span"
                          onClick={() => handleUserClick(userId)}
                          variant="button"
                          color="info"
                          fontWeight="medium"
                          sx={{ cursor: "pointer" }}
                        >
                          {label}
                        </MDTypography>
                      );
                    }
                  : item.renderValue ||
                    ((val) =>
                      val !== null && val !== undefined && val !== "" ? String(val) : "-")
              }
            />
          </Grid>
        ))}
      </Grid>
      </>
    );
    return (
      <UiDetailSection
        title={title}
        legacy={
          <MDBox
            mb={3}
            p={3}
            borderRadius="lg"
            sx={{
              backgroundColor: "#ffffff",
              boxShadow: 3,
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium" color="dark">
                {title}
              </MDTypography>
            </MDBox>
            {sectionBody}
          </MDBox>
        }
      >
        {sectionBody}
      </UiDetailSection>
    );
  };

  const headerButtons = useMemo(
    () => (
      <MDBox display="flex" gap={1}>
        {isEditing ? (
          <>
            <Tooltip title="Cancel" placement="top">
              <IconButton onClick={handleDiscardChanges} sx={commonIconButtonStyles}>
                <Close sx={commonIconStyles} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save" placement="top">
              <span>
                <IconButton
                  onClick={handleSaveChanges}
                  disabled={isSaveLoading}
                  sx={commonIconButtonStyles}
                >
                  {isSaveLoading ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    <Save sx={commonIconStyles} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Edit" placement="top">
            <IconButton onClick={() => setIsEditing(true)} sx={commonIconButtonStyles}>
              <Edit sx={commonIconStyles} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete" placement="top">
          <span>
            <IconButton
              onClick={() => setIsDeleteModalOpen(true)}
              disabled={isDeleteLoading}
              sx={commonIconButtonStyles}
            >
              <Delete sx={commonIconStyles} />
            </IconButton>
          </span>
        </Tooltip>
      </MDBox>
    ),
    [isEditing, isSaveLoading, isDeleteLoading]
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          entityType="payment"
          title="Loading Payment Transaction"
          message="Please wait while we fetch the payment transaction information..."
        />
      </DashboardLayout>
    );
  }

  if (!transaction) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <MDTypography>Payment Transaction not found</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h5" color="white" marginLeft={1} marginBottom={2}>
                    {transaction.name || nameFromState || "Payment Transaction"}
                  </MDTypography>
                  {headerButtons}
                </MDBox>
                <MDBox>
                  <MDTypography variant="button" color="white" mb={2}>
                    {transaction.payment_status || "Payment Pending"}
                  </MDTypography>
                  <ThemedCustomStepper
                    activeStep={activeStep}
                    steps={PAYMENT_STATUS_STEPS}
                    onStepClick={handleStatusStepClick}
                    successStep="Paid"
                    failureStep="Failed"
                  />
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                {renderSection("Basic Information", [
                  { label: "Name", field: "name" },
                  {
                    label: "Order Id",
                    field: "travel_order_id",
                    readOnly: true,
                    renderValue: (val) => transaction.travel_order?.name || val || "-",
                  },
                  {
                    label: "Invoice",
                    field: "invoice_id",
                    readOnly: true,
                    renderValue: (val) => transaction.invoice?.invoice_number || val || "-",
                  },
                  {
                    label: "Amount",
                    field: "amount",
                    renderValue: (val) => formatAmount(val),
                  },
                  {
                    label: "Payment Status",
                    field: "payment_status",
                    options: PAYMENT_STATUS_OPTIONS,
                  },
                  {
                    label: "Payment Date",
                    field: "payment_date",
                    readOnly: true,
                    renderValue: (val) => (val ? formatDate(val, "datetime") : "-"),
                  },
                ])}

                {renderSection("Payment Details", [
                  {
                    label: "Payment Method",
                    field: "payment_method",
                    options: PAYMENT_METHOD_OPTIONS,
                  },
                  {
                    label: "Payment Mode",
                    field: "payment_mode",
                    options: PAYMENT_MODE_OPTIONS,
                  },
                  {
                    label: "Payment Type",
                    field: "payment_type",
                    options: PAYMENT_TYPE_OPTIONS,
                  },
                  { label: "Transaction ID", field: "transaction_id" },
                  {
                    label: "Installment Number",
                    field: "installment_number",
                    renderValue: (val) =>
                      val !== null && val !== undefined && val !== ""
                        ? `Installment ${val}`
                        : "-",
                  },
                  {
                    label: "Payment Due Date",
                    field: "payment_due_date",
                    readOnly: true,
                    renderValue: (val) => (val ? formatDate(val, "date") : "-"),
                  },
                  {
                    label: "Surcharge Amount",
                    field: "surcharge_amount",
                    renderValue: (val) => formatAmount(val),
                  },
                  { label: "Stripe Intent ID", field: "stripe_intent_id" },
                ])}

                {renderSection("Audit Information", [
                  { label: "Created By", field: "created_by_name", readOnly: true },
                  { label: "Modified By", field: "updated_by_name", readOnly: true },
                  {
                    label: "Created Date",
                    field: "created_at",
                    readOnly: true,
                    renderValue: (val) => (val ? formatDate(val, "datetime") : "-"),
                  },
                  {
                    label: "Modified Date",
                    field: "updated_at",
                    readOnly: true,
                    renderValue: (val) => (val ? formatDate(val, "datetime") : "-"),
                  },
                ])}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 420 },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
          }}
        >
          <MDTypography variant="h6" mb={1}>
            Delete Payment Transaction
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={3}>
            Are you sure you want to delete this payment transaction? Related invoice and order
            payment status will be recalculated.
          </MDTypography>
          <MDBox display="flex" justifyContent="flex-end" gap={1}>
            <MDButton color="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="error"
              onClick={handleDelete}
              disabled={isDeleteLoading}
            >
              {isDeleteLoading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
            </MDButton>
          </MDBox>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default PaymentTransactionDetail;
